import { useMediaQuery } from "@mui/material";
import { getDefaultApplicationGuideContent } from "pages/customForms/components/DefaultGuides";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  createGuide,
  deleteGuide,
  getGuides,
  updateGuide,
} from "reduxStore/actions/guideActions";
import { checkRoles } from "utils/permissions";
import { v4 as uuidv4 } from "uuid";
import Tracking from "assets/images/Tracking.png";

export const useApplicationGuide = () => {
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  // State and Redux selectors
  const user = useSelector((state) => state.user);

  const { isSystemAdmin } = checkRoles(user);
  const guides = useSelector((state) => state.guides);

  // Local state
  const [openGuideEdit, setOpenGuideEdit] = useState(false);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [description, setDescription] = useState("");
  const [guideId, setGuideId] = useState("");
  const [isLoadingGuides, setIsLoadingGuides] = useState(true);

  const containerRefs = useRef([]);
  const defaultApplicationGuideContent = getDefaultApplicationGuideContent;

  const defaultImageURL = Tracking;

  // Open/close the edit dialog
  const handleOpenGuideEdit = () => setOpenGuideEdit(true);
  const handleCloseGuideEdit = () => setOpenGuideEdit(false);

  // Preprocess content before saving
  const preprocessContentForSave = (content) =>
    content.replace(/<li data-list="bullet">/g, "<li>");

  // Sanitize content for rendering
  const sanitizeContent = (htmlContent) => {
    const updatedContent = DOMPurify.sanitize(htmlContent).replace(
      /(<p><br><\/p>)+/g,
      "<br>"
    );
    return { __html: updatedContent };
  };

  // Save or update a guide
  const handleSaveGuide = async () => {
    try {
      const guideData = {
        title: "Appraisal Guide",
        description,
        content: contentBlocks.map((block) =>
          block.type === "text"
            ? { ...block, data: preprocessContentForSave(block.data) }
            : block
        ),
      };

      const action = guideId
        ? updateGuide({ ...guideData, _id: guideId })
        : createGuide(guideData);

      const result = await dispatch(action);
      if (result) {
        setGuideId(result._id); // Update guide ID after saving
      }

      handleCloseGuideEdit();
    } catch (error) {
      console.error("Error saving guide:", error);
    }
  };

  // Delete a guide
  const handleDeleteGuide = async () => {
    if (window.confirm("Are you sure you want to delete this guide?")) {
      const success = await dispatch(deleteGuide(guideId));
      if (success) {
        setGuideId("");
        setContentBlocks(defaultApplicationGuideContent);
        // Reset to default
        alert("Guide deleted successfully");
      } else {
        alert("Failed to delete guide");
      }
    }
  };

  // Add a new text block
  const addTextBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "text", data: "" },
    ]);

  // Add a new image block

  const addImageBlock = () =>
    setContentBlocks([
      ...contentBlocks,
      { id: uuidv4(), type: "image", data: null },
    ]);

  // Update a specific block's data
  const updateBlockData = (index, data) => {
    if (index === -1 && Array.isArray(data)) {
      setContentBlocks(data); // replace all
    } else {
      setContentBlocks((prevBlocks) => {
        const updatedBlocks = [...prevBlocks];
        updatedBlocks[index] = { ...updatedBlocks[index], data };
        return updatedBlocks;
      });
    }
  };

  // Remove a specific block
  const removeBlock = (index) => {
    setContentBlocks((prevBlocks) => prevBlocks.filter((_, i) => i !== index));
  };

  const swapPublicToXxLarge = (url) =>
    url ? url.replace(/\/public$/, "/xxLarge") : defaultImageURL;

  // Fetch guides on initial load
  useEffect(() => {
    const fetchGuides = async () => {
      setIsLoadingGuides(true);
      await dispatch(getGuides());
      setIsLoadingGuides(false);
    };
    fetchGuides();
  }, [dispatch]);

  // Set guide content after guides are fetched
  useEffect(() => {
    if (!isLoadingGuides) {
      const applicationGuide = guides.find(
        (guide) => guide.title === "Appraisal Guide"
      );

      if (applicationGuide) {
        setContentBlocks(
          (applicationGuide.content || []).map((block) => ({
            ...block,
            id: block.id || uuidv4(),
          }))
        );
        setDescription(applicationGuide.description);
        setGuideId(applicationGuide._id);
      } else {
        // Fallback to default content if no specific guide is found
        setContentBlocks(defaultApplicationGuideContent);

        setDescription("Default Appraisal Guide");
        setGuideId(""); // Reset guide ID if no match
      }
    }
  }, [guides, isLoadingGuides]);

  // Render text blocks inside their refs after changes
  useEffect(() => {
    contentBlocks.forEach((block, index) => {
      if (block.type === "text" && containerRefs.current[index]) {
        const blockRef = containerRefs.current[index];
        blockRef.innerHTML = block.data;
      }
    });
  }, [contentBlocks]);

  return {
    isLargeScreen,
    isSystemAdmin,
    openGuideEdit,
    handleOpenGuideEdit,
    handleCloseGuideEdit,
    handleSaveGuide,
    handleDeleteGuide,
    addTextBlock,
    addImageBlock,
    updateBlockData,
    removeBlock,
    contentBlocks,
    sanitizeContent,
    isLoadingGuides,
    defaultImageURL,
    guideId,
    swapPublicToXxLarge,
  };
};
